import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  extractDefaultStoreId,
  extractNumber,
  extractNumberArr,
  HxCategoryService,
  HxFioPipe,
  HxReportService,
  HxUiLabelPipe,
  IncomeOutcomeBodyModel,
  IncomeOutcomeHeaderModel,
  isoDate,
  StoreBasicModel,
  toFixed,
  toRouterQueryParams
} from 'hx-services';
import { HxDateSelectComponent, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe } from '@jsverse/transloco';


/**
 * Report component of income-outcome cakes
 * Отчёт Приход-расход
 */
@Component({
    selector: 'app-income-outcome.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './income-outcome.component.html',
    styleUrls: ['./income-outcome.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateSelectComponent, HxProductCategorySelectComponent, NgIf, NgFor, NgbTooltip, NgClass, HxLoaderComponent, TranslocoPipe, HxFioPipe, HxUiLabelPipe]
})
export class IncomeOutcomeComponent implements OnInit, OnDestroy {
  list: IncomeOutcomeBodyModel[] = [];
  isLoading = {
    list: false
  };
  params: { storeId?: number, date?: string, categoryIds?: number[] } = {};
  headers?: IncomeOutcomeHeaderModel;
  brandId: number;
  private sub: Subscription;
  private map = new Map<number, number>();

  static roundUp(num: number): number {
    return Math.ceil(num * 10) / 10;
  }

  constructor(
    private reportService: HxReportService,
    private categoryService: HxCategoryService,
    private router: Router,
    private aRoute: ActivatedRoute,
  ) {
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap);
      this.params.date = paramMap.get('date') ?? isoDate(new Date());
      this.params.storeId = extractNumber('storeId', paramMap) ?? extractDefaultStoreId();
      if (this.params.storeId && this.params.categoryIds.length > 0) {
        this.loadList();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  resetFilter() {
    this.params = {date: isoDate(Date.now())};
    this.navigate();
  }

  isDayRemainderValid(productInfoId: number, selectedDayRemainder: number): boolean {
    return IncomeOutcomeComponent.roundUp(this.map.get(productInfoId)) === IncomeOutcomeComponent.roundUp(selectedDayRemainder);
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (Array.isArray(store)) {
      return;
    }
    if (!store || this.brandId !== store?.brandId) {
      this.params.categoryIds = [];
    }
    this.brandId = store?.brandId;
    this.navigate();
  }

  private loadList() {
    this.list = [];
    this.isLoading.list = true;
    this.reportService.getIncomeOutcome(this.params).then(result => {
      this.headers = result.header;
      this.list = result.list ?? [];
      this.list.forEach(el => {
        el.dayBeforeRemainder = 0;
        el.selectedDayRemainder = 0;

        el.stores.forEach(item => {
          if (item.dayBeforeRemainder) {
            el.dayBeforeRemainder += item.dayBeforeRemainder;
          }
          if (item.selectedDayRemainder) {
            el.selectedDayRemainder += item.selectedDayRemainder;
          }
        });

        const income = el.stores.filter(s => !isNaN(s.income)).map(r => r.income).reduce((sum, c) => sum + c, 0);
        const discardedAmount = el.stores.filter(s => !isNaN(s.discardedAmount)).map(r => r.discardedAmount).reduce((sum, c) => sum + c, 0);
        const refundedAmount = el.stores.filter(s => !isNaN(s.refundedAmount)).map(r => r.refundedAmount).reduce((sum, c) => sum + c, 0);
        const exportAmount = el.stores.filter(s => !isNaN(s.exportAmount)).map(r => r.exportAmount).reduce((sum, c) => sum + c, 0);
        const employeeDeliverySum = el.employees.filter(s => !isNaN(s.deliveryAmount)).map(r => r.deliveryAmount).reduce((sum, c) => sum + c, 0);
        const confectionaryAmount = el.stores.filter(s => !isNaN(s.confectionaryAmount)).map(r => r.confectionaryAmount).reduce((sum, c) => sum + c, 0);
        const inventoryAmount = el.stores.filter(s => !isNaN(s.inventoryAmount)).map(r => r.inventoryAmount).reduce((sum, c) => sum + c, 0);

        const totalSum = ((((el.dayBeforeRemainder + income) - exportAmount) - (employeeDeliverySum - (refundedAmount - discardedAmount))) - confectionaryAmount) + inventoryAmount;

        this.map.set(el.productInfo.id, toFixed(totalSum));
      });

      this.isLoading.list = false;
    }).catch(() => this.isLoading.list = false);
  }
}
